<script setup lang="ts">
import { AdminReviewSchema, UiAvatar, UiButton, UiCard, UiChip, UiSwitch } from '@shopexperts/shared';
import { z } from 'zod';
import { computed } from 'vue';
import ReviewerInfo from './shared/ReviewerInfo.vue';
import RatingDisplay from './shared/RatingDisplay.vue';
import ReviewMetadata from './shared/ReviewMetadata.vue';
import { useSetReviewShortTextVisibility } from '@/app/composables/reviews/useSetReviewShortTextVisibility';

type Review = z.infer<typeof AdminReviewSchema>;

const props = withDefaults(defineProps<{
  review: Review;
  variant?: 'pending' | 'published';
}>(), {
  variant: 'pending'
});

const emit = defineEmits<{
  edit: [review: Review];
  approve: [id: number];
  decline: [id: number];
  unpublish: [id: number];
  delete: [id: number];
}>();

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const statusChip = computed(() => {
  return props.review.status === 'pending' 
    ? { variant: 'warn' as const, text: 'Pending Approval' }
    : props.review.status === 'published' ? { variant: 'success' as const, text: 'Published' }
    : props.review.status === 'unpublished' ? { variant: 'default' as const, text: 'Unpublished' } 
    : { variant: 'warn' as const, text: 'Pending Approval' };
});

const shortTextVisibility = useSetReviewShortTextVisibility();
const showShortTextControl = computed(() => props.review.public_text_hidden === true || props.review.short_text_grandfathered === true);

const editReview = () => emit('edit', props.review);
const approveReview = () => emit('approve', props.review.id);
const declineReview = () => emit('decline', props.review.id);
const unpublishReview = () => emit('unpublish', props.review.id);
const deleteReview = () => emit('delete', props.review.id);
</script>

<template>
  <UiCard padding="md">
    <div class="flex items-start justify-between gap-4 mb-4">
      <ReviewerInfo 
        :name="props.review.client.name"
        :avatar-url="props.review.client.avatar_url"
        :company-name="props.review.client.company_name"
        :company-website="props.review.client.company_website"
        :is-repeat-client="props.review.client.is_repeat_client ?? false"
        :is-hired-on-shopexperts="props.review.is_hired_on_shopexperts ?? false"
      />

      <!-- Status and Date -->
      <div class="flex flex-col items-end gap-1.5 shrink-0">
        <UiChip :variant="statusChip.variant" size="sm" :text="statusChip.text" />
        <UiChip v-if="props.review.public_text_hidden" variant="neutral" size="sm" text="Hidden publicly (short text)" />
        <span class="text-xs text-gray-500 whitespace-nowrap">
          Posted: {{ formatDate(props.review.created_at) }}
        </span>
      </div>
    </div>

    <!-- Rating -->
    <div class="mb-3">
      <RatingDisplay 
        label="Rating:"
        :rating="props.review.overall_rating"
      />
    </div>

    <!-- Review Text -->
    <p class="text-gray-700 leading-relaxed mb-4">
      {{ props.review.review_text }}
    </p>

    <UiSwitch
      v-if="showShortTextControl"
      class="mb-4"
      label="Keep public despite short text"
      :model-value="props.review.short_text_grandfathered === true"
      :disabled="shortTextVisibility.isPending.value"
      @update:model-value="shortTextVisibility.mutate({ reviewId: props.review.id, keepPublic: $event })"
    />

    <!-- Metadata -->
    <!-- Todo add apps and themes-->
    <ReviewMetadata
      :project-value="props.review.project_value_range"
      :review-source="props.review.review_source"
      :imported-from="props.review.imported_from"
    />

    <!-- Footer - Expert Info and Actions -->
    <div class="flex items-center justify-between pt-5 border-t border-gray-200">
      <div class="flex items-center gap-3">
        <UiAvatar
          :url="props.review.expert.profile_picture || undefined"
          :name="props.review.expert.name"
          size="xs"
        />
        <div>
          <div class="text-xs text-gray-500 mb-0.5">Expert</div>
          <div class="font-semibold text-gray-900">{{ props.review.expert.name }}</div>
          <div class="text-sm text-gray-600">
            {{ props.review.expert.role || (props.review.expert.type === 'freelancer' ? 'Freelancer' : 'Agency') }}
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-2">
        <UiButton
          variant="outline"
          @click="editReview"
          label="Edit Review"
        />
        <template v-if="variant === 'pending'">
          <UiButton
            variant="outline"
            @click="declineReview"
            label="Decline Review"
          />
          <UiButton
            @click="approveReview"
            label="Approve Review"
          />
        </template>
        <template v-else>
          <UiButton
            variant="outline"
            @click="deleteReview"
            label="Delete Review"
          />
          <UiButton
            variant="outline"
            v-if="props.review.status === 'published'"
            @click="unpublishReview"
            label="Unpublish Review"
          />
        </template>
      </div>
    </div>
  </UiCard>
</template>
